import classNames from "classnames"
import { colorsToFontColorClass, TextColor } from "components/DesignSystem"
import { TranslatableText, TranslatableTextProps } from "components/TranslatableText/TranslatableText"
import { JSX } from "react"

export type BodyTextType = "paragraph" | "inline" | "block" | "inline-block"
export type BodyTextWeight = "regular" | "medium"

export interface BodyTextProps extends TranslatableTextProps {
  /**
   *  The level of body text. Our design system defines 3 levels
   *  starting at 1. The lower levels are more important.
   */
  level?: number

  /**
   *  The weight of the text.
   */
  weight?: BodyTextWeight

  /**
   *  The color of the checklist.
   */
  color?: TextColor | "inherit"

  /**
   *  The type of the text.
   */
  type?: BodyTextType

  /**
   * Text transformation, e.g. uppercase.
   */
  transform?: "uppercase" | "lowercase" | "capitalize"

  /**
   * Enables text truncation with ellipsis (...) for dynamic flex layouts.
   *
   * When enabled, the component wraps itself in a container with `min-w-0 flex-1`
   * and applies `truncate` (single line) or `line-clamp-N` (multi-line) classes.
   *
   * IMPORTANT: For truncation to work in nested flex layouts, the PARENT flex
   * container must have `min-w-0` applied. This is due to flexbox's default
   * `min-width: auto` behavior which prevents flex items from shrinking below
   * their content's intrinsic size.
   *
   * @example
   * // Parent flex container MUST have min-w-0:
   * <div className="flex min-w-0 gap-2">
   *   <div>{icon}</div>
   *   <P truncate>{longFileName}</P>
   * </div>
   *
   * @see IT-3556 for implementation details
   */
  truncate?: boolean

  /**
   * Number of lines to show before truncating (requires truncate={true}).
   * Defaults to 1 (single line truncation).
   *
   * @example
   * <P truncate maxLines={2}>{longDescription}</P>
   */
  maxLines?: "none" | 1 | 2 | 3

  /**
   * Preserves `\n` newlines from the rendered text (maps to `whitespace-pre-line`).
   * Use this when a translation key intentionally contains line breaks. Prefer
   * the `MultilineText` alias for the common paragraph case.
   */
  preserveNewlines?: boolean

  /**
   * Lets the text wrap onto multiple lines, breaking long words if needed, even
   * inside a `whitespace-nowrap` ancestor such as a table row (maps to
   * `whitespace-normal break-words`). Mutually exclusive with `truncate` and
   * `preserveNewlines`.
   */
  wrap?: boolean

  /**
   *  Custom styles.
   *  @deprecated The component support passing CSS classes, but it's inherently
   *  problematic as it means the component has to make sure that it can work
   *  with any kind of styles and can take many different unexpected roles
   *  (it could be suddenly restyled as table or similar).
   *  While we support for now, cause our components don't correctly follow
   *  SOLID nor DRY principles, we want to discourage as much as possible injecting
   *  custom styles into basic components.
   */
  className?: string
}

/**
 *  This component is a direct representation of body text styles in a form
 *  of a component. It should be used in most (if not all) cases when we want
 *  to display a regular test on the webpage. However, the component wouldn't
 *  be used directly. Instead `<P>` or `<Text>` components should be used to
 *  provide better contextual reason for the text.
 *
 *  The component is implemented based on our design system.
 *  @see https://www.figma.com/design/mVCTjl16dXiTC880fA4GHT/Mijnpraktijk-design-system?node-id=3-407&m=dev
 */
export const BodyText = ({
  level = 2,
  weight = "regular",
  type = "paragraph",
  color = "inherit",
  transform,
  truncate = false,
  maxLines = 1,
  preserveNewlines = false,
  wrap = false,
  className,
  ...props
}: BodyTextProps) => {
  const fontColor = color === "inherit" ? "text-inherit" : colorsToFontColorClass[color]

  // Add truncate class if enabled
  const truncateClass = truncate ? (maxLines === 1 ? "truncate" : `line-clamp-${maxLines}`) : undefined

  const whitespaceClass = preserveNewlines ? "whitespace-pre-line" : wrap ? "whitespace-normal break-words" : undefined

  const cssClass = classNames(
    `text-body${level}/${weight}`,
    fontColor,
    transform,
    truncateClass,
    whitespaceClass,
    className
  )

  // Render the text element
  let textElement: JSX.Element

  if (type === "paragraph") {
    textElement = (
      <p className={cssClass}>
        <TranslatableText {...props} />
      </p>
    )
  } else if (type === "block") {
    textElement = (
      <div className={cssClass}>
        <TranslatableText {...props} />
      </div>
    )
  } else {
    textElement = (
      <span className={classNames(cssClass, { "inline-block": type === "inline-block" })}>
        <TranslatableText {...props} />
      </span>
    )
  }

  // Wrap in a flex container if truncate is enabled
  if (truncate) {
    return <div className="min-w-0 flex-1">{textElement}</div>
  }

  return textElement
}

/**
 *  An alias component to render a paragraph text.
 */
export const P = (props: Omit<BodyTextProps, "type">) => {
  return <BodyText {...props} type="paragraph" />
}

/**
 *  An alias component to render an inline text.
 */
export const Text = (props: Omit<BodyTextProps, "type">) => {
  return <BodyText {...props} type="inline" />
}
